import { useState } from "react";
import { TableHeading } from "@/components/Table";
import type { Action } from "@/components/MenuButton";
import { useCanManageCommitteesBool } from "@/hooks/useCapabilities";
import type { CommitteeCatalogScope } from "../committeeScope";
import type { CommitteeCatalogTab } from "../types";
import CommitteesCatalogTable from "./CommitteesCatalogTable";
import GroupEmailSheet from "./GroupEmailSheet";
import { useCanAccessGroupEmail } from "./useCanAccessGroupEmail";

type CommitteeCatalogSectionProps = {
  catalogTab: CommitteeCatalogTab;
  heading?: string;
  body?: string;
  sectionId?: string;
  sectionName?: string;
  canManageCommittees?: boolean;
  committeeScope?: CommitteeCatalogScope;
};

export default function CommitteeCatalogSection({
  catalogTab,
  heading,
  body,
  sectionId,
  sectionName,
  canManageCommittees: canManageCommitteesProp,
  committeeScope = "national"
}: CommitteeCatalogSectionProps) {
  const canManageFromPermission = useCanManageCommitteesBool();
  const canManageCommittees = canManageCommitteesProp ?? canManageFromPermission;
  const canAccessGroupEmail = useCanAccessGroupEmail();
  const [addCommitteeDialogOpen, setAddCommitteeDialogOpen] = useState(false);
  const [canAddCommittee, setCanAddCommittee] = useState(() => Boolean(canManageCommittees && sectionId));
  const [groupEmailOpen, setGroupEmailOpen] = useState(false);

  const actions =
    canManageCommittees && canAddCommittee
      ? [
          {
            icon: "add" as const,
            label: "Add Committee",
            onClick: () => setAddCommitteeDialogOpen(true),
            testId: "add-committee-button"
          }
        ]
      : [];

  const tableActions: Action[] = canAccessGroupEmail
    ? [
        {
          text: "Group Email",
          iconName: "mail",
          menuOnly: true,
          section: "Contact",
          testId: "group-email-button",
          onClick: () => setGroupEmailOpen(true)
        }
      ]
    : [];

  return (
    <div className="w-full">
      <div className="pt-12" />
      {heading || body || actions.length > 0 ? (
        <TableHeading heading={heading} body={body} actions={actions} topSpacing="none" />
      ) : null}
      <CommitteesCatalogTable
        catalogTab={catalogTab}
        sectionId={sectionId}
        sectionName={sectionName}
        canManageCommittees={canManageCommittees}
        committeeScope={committeeScope}
        addDialogOpen={addCommitteeDialogOpen}
        onAddDialogOpenChange={setAddCommitteeDialogOpen}
        onCanAddCommitteeChange={setCanAddCommittee}
        tableActions={tableActions}
      />
      <GroupEmailSheet
        open={groupEmailOpen}
        onClose={() => setGroupEmailOpen(false)}
        preferredSectionId={committeeScope === "section" ? sectionId : undefined}
      />
    </div>
  );
}
